import { useContext, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { createMemoryRouter, Outlet, RouterProvider, useLocation, type RouteObject } from "react-router";
import { DraftGuardContext } from "../../../features/processAuthoring/draft/draftGuardContext";
import UnsavedDraftGuard from "../../../features/processAuthoring/draft/UnsavedDraftGuard";
import { sidebarSectionOfPath } from "../../appSections";
import DetourBack from "../../detour/DetourBack";
import Sidebar from "../../Sidebar";
import { PaneSidebarContext, type PaneSidebar } from "../paneSidebar";
import type { WorkspaceController } from "../workspaceController";
import { WorkspaceContext } from "../workspaceContext";
import type { AppPath, TabId } from "../workspaceTypes";
import { modifiedLinkClick } from "./modifiedLinkClick";

interface TabHostProps {
  readonly controller: WorkspaceController;
  readonly tabId: TabId;
  /** Where the tab starts when it mounts; from then on its router leads. */
  readonly route: AppPath;
  readonly tabRoutes: readonly RouteObject[];
}

/**
 * One work tab (F90): the app's pages under a memory router of their own, so `useParams`,
 * `Link` and `useNavigate` work in it as in a browser tab, and its draft guard guards it.
 * The owner learns where it goes and keeps the window's address and history.
 */
export default function TabHost({ controller, tabId, route, tabRoutes }: TabHostProps) {
  const [router] = useState(() =>
    createMemoryRouter([{ element: <TabPage controller={controller} tabId={tabId} />, children: [...tabRoutes] }], { initialEntries: [route] }),
  );
  useEffect(() => controller.mountTab(tabId, router), [controller, tabId, router]);
  const isActive = useSyncExternalStore(controller.subscribe, () => controller.getSnapshot().activeTab === tabId);
  const workspace = useMemo(() => controller.workspaceOf(tabId, isActive), [controller, tabId, isActive]);
  return (
    <WorkspaceContext.Provider value={workspace}>
      <RouterProvider router={router} />
    </WorkspaceContext.Provider>
  );
}

/**
 * A tab's frame: its draft guard, its section's sidebar and its page. The sidebar belongs to
 * the tab, so each pane shows its own tab's sidebar, open or closed as that tab keeps it
 * (Johannes, 29 Oct). The page's frame is a size container, so a page lays out by the room
 * beside the sidebar (`pane-md:`, `pane-lg:`); the page and the sidebar scroll on their own (F45).
 */
function TabPage({ controller, tabId }: { controller: WorkspaceController; tabId: TabId }) {
  const openModified = modifiedLinkClick((route, placement) => { controller.open(tabId, route, placement); });
  // Pages outside the sections (projects, items, the profile) and sections without a sidebar have nothing for it.
  const section = sidebarSectionOfPath(useLocation().pathname);
  const sidebarOpen = useSyncExternalStore(controller.subscribe, () => !controller.getSnapshot().closedSidebars.includes(tabId));
  const paneSidebar = useMemo<PaneSidebar>(() => ({ isOpen: sidebarOpen, open: () => { controller.setSidebarOpen(tabId, true); } }), [controller, tabId, sidebarOpen]);
  return (
    <UnsavedDraftGuard>
      <TabGuard controller={controller} tabId={tabId} />
      <PaneSidebarContext.Provider value={paneSidebar}>
        <div className="flex h-full min-h-0 overflow-clip" onClickCapture={openModified} onAuxClickCapture={openModified}>
          {section === null ? null : (
            <Sidebar open={sidebarOpen} section={section} onOpenChange={(open) => { controller.setSidebarOpen(tabId, open); }} />
          )}
          <div className="app-main h-full min-h-0 min-w-0 flex-1 overflow-auto px-6 [container-type:inline-size]">
            {/* One back control for every detour page, in the tab's frame rather than per page (F87, F80). */}
            <DetourBack />
            <Outlet />
          </div>
        </div>
      </PaneSidebarContext.Provider>
    </UnsavedDraftGuard>
  );
}

/** Lends the tab's draft guard to the owner, which asks it before the tab closes. */
function TabGuard({ controller, tabId }: { controller: WorkspaceController; tabId: TabId }) {
  const { requestClose } = useContext(DraftGuardContext);
  useEffect(() => controller.guardTab(tabId, requestClose), [controller, tabId, requestClose]);
  return null;
}
